import { LogService } from "..";
import Cookies from "js-cookie";
import { ConsentDataFormat, ConsentStorageData, ConsentType } from "./consent-type";

export enum StorageType {
	Cookie = "cookie",
	Memory = "memory",
}

export enum StandardConsent {
	Convenience = "convenience",
	Analysis = "analysis",
	Marketing = "marketing",
}

export enum StaticConsent {
	PartnerDataSharing = "partnerDataSharing",
	PartnerCommonConsent = "partnerCommonConsent",
}

export type SameSiteTypes = "strict" | "Strict" | "lax" | "Lax" | "none" | "None";

type WriteConsentsFunction = (
	consentDataFormat: ConsentDataFormat,
	cookieName: string,
	cookieSameSite: SameSiteTypes,
	cookieSecure: boolean,
	consentDomain: string,
	consentPath: string,
	consentLifetime: number,
) => Promise<void>;
type RemoveConsentsFunction = (consentDataName?: string) => Promise<void>;

class ConsentServiceController {
	inMemoryConsents: ConsentDataFormat;

	constructor() {
		this.inMemoryConsents = undefined;
	}

	/**
	 * Sets all given consents
	 * @param consents
	 * @param cookieVersion
	 * @param storageTypes
	 * @param cookieName
	 * @param cookieSameSite
	 * @param cookieSecure
	 * @param cookieDomain
	 * @param cookiePath
	 * @param cookieLifetime
	 */
	async storeConsents(
		consents: ConsentType[],
		cookieVersion: number,
		storageTypes: StorageType[],
		cookieName: string,
		cookieSameSite: SameSiteTypes,
		cookieSecure: boolean,
		cookieDomain: string,
		cookiePath: string,
		cookieLifetime: number,
	): Promise<void> {
		LogService.log.debug("Store these consents to:", storageTypes, consents);

		const consentData = this.createConsentDataFormat(consents, cookieLifetime, cookieVersion);

		for (const storageType of storageTypes) {
			switch (storageType) {
				case StorageType.Cookie:
					await this.writeConsentsToCookie(consentData, cookieName, cookieSameSite, cookieSecure, cookieDomain, cookiePath, cookieLifetime);
					break;
				case StorageType.Memory:
					//Currently writeConsentsToCookie and writeConsentsToMemory use the same type. As we don't have clear requirements for writeConsentsToMemory type we pass to it all the values we pass to
					await this.writeConsentsToMemory(consentData, cookieName, cookieSameSite, cookieSecure, cookieDomain, cookiePath, cookieLifetime);
					break;
			}
		}
	}

	/**
	 * Removes all consents in the specified storage destinations.
	 * @param storageTypes
	 * @param cookieName
	 */
	async removeConsents(storageTypes: StorageType[], cookieName: string): Promise<void> {
		LogService.log.debug("Remove all consents from:", storageTypes);

		for (const storageType of storageTypes) {
			switch (storageType) {
				case StorageType.Cookie:
					await this.removeConsentsCookie(cookieName);
					break;
				case StorageType.Memory:
					await this.removeConsentsMemory();
					break;
			}
		}
	}

	/**
	 * Gets all read consents
	 */
	async restoreConsents(storageTypes: StorageType[], cookieName: string): Promise<{ storageData: ConsentStorageData; consents: ConsentType[] }> {
		let restoredConsents: ConsentType[] = [];
		// restoredStorageData is currently not in use. We might want to delete it when refactoring at some point
		let restoredStorageData: ConsentStorageData;
		LogService.log.debug("Restore consents from:", storageTypes);

		for (const storageType of storageTypes) {
			switch (storageType) {
				case StorageType.Cookie: {
					const cookieConsents: ConsentDataFormat = await this.readConsentsFromCookie(cookieName);

					restoredConsents.push(...this.getAllConsentsAsArrayFromDataFormat(cookieConsents));

					if (cookieConsents) {
						restoredStorageData = {
							storageType: StorageType.Cookie,
							timestamp: cookieConsents.timestamp,
							timeToLive: cookieConsents.timeToLive,
							version: cookieConsents.version,
						};
					}
					break;
				}
				case StorageType.Memory: {
					const memoryConsents: ConsentDataFormat = await this.readConsentsFromMemory();

					restoredConsents.push(...this.getAllConsentsAsArrayFromDataFormat(memoryConsents));

					if (memoryConsents) {
						restoredStorageData = {
							storageType: StorageType.Memory,
							timestamp: memoryConsents.timestamp,
							timeToLive: memoryConsents.timeToLive,
							version: memoryConsents.version,
						};
					}
					break;
				}
			}
		}

		const consentsWithoutDuplicates: ConsentType[] = this.removeObjectDuplicatesInArray(restoredConsents);

		LogService.log.debug("Restored consents:", consentsWithoutDuplicates);
		LogService.log.debug("Storage data:", restoredStorageData);

		return { storageData: restoredStorageData, consents: consentsWithoutDuplicates };
	}

	/**
	 * ConsentType factory that returns one object
	 * @param name The name of the consent e.g. 'comfort', 'marketing' - must be unique
	 * @param userConsents Sets if the user consents (true) to this specific consent or not
	 */
	consentFactory(name: string, userConsents: boolean): ConsentType {
		return {
			name: name,
			userConsents: userConsents,
		};
	}

	/**
	 * Returns one dummy consent - for development purposes
	 */
	createDummyConsent(): ConsentType {
		return this.consentFactory("other", false);
	}

	/**
	 * Returns an array with dummy consent objects - for development purposes.
	 * TODO: In production this method can be moved to the tests as a test util when not used anymore in the code.
	 */
	createDummyConsents(): ConsentType[] {
		return [
			this.consentFactory(StandardConsent.Marketing, false),
			this.consentFactory(StandardConsent.Convenience, true),
			this.consentFactory(StandardConsent.Analysis, true),
			this.consentFactory("other", false),
			this.consentFactory("another", true),
		];
	}

	/**
	 * Extracts and returns all consents as an array.
	 * @param consentDataFormat
	 * @private
	 */
	private getAllConsentsAsArrayFromDataFormat(consentDataFormat: ConsentDataFormat): ConsentType[] {
		let customConsents: ConsentType[] = [];
		let marketingConsentsMap: ConsentType = undefined;
		let convenienceConsentsMap: ConsentType = undefined;
		let analysisConsentsMap: ConsentType = undefined;
		if (consentDataFormat && consentDataFormat.consent) {
			if (consentDataFormat.consent.custom) {
				customConsents.push(...consentDataFormat.consent.custom);
			}

			if (consentDataFormat.consent.marketing !== undefined) {
				marketingConsentsMap = {
					name: StandardConsent.Marketing,
					userConsents: consentDataFormat.consent.marketing,
				};
			}

			if (consentDataFormat.consent.convenience !== undefined) {
				analysisConsentsMap = {
					name: StandardConsent.Analysis,
					userConsents: consentDataFormat.consent.analysis,
				};
			}

			if (consentDataFormat.consent.convenience !== undefined) {
				convenienceConsentsMap = {
					name: StandardConsent.Convenience,
					userConsents: consentDataFormat.consent.convenience,
				};
			}
		}

		// This array also defines the order of the returned consent categories in the UI
		return [convenienceConsentsMap, analysisConsentsMap, marketingConsentsMap, ...customConsents].filter(Boolean);
	}

	private writeConsentsToCookie: WriteConsentsFunction = async (consents, cookieName, cookieSameSite, cookieSecure, cookieDomain, cookiePath, cookieLifetime) => {
		LogService.log.debug("Write consents to cookie:", consents);
		Cookies.set(cookieName, JSON.stringify(consents), { secure: cookieSecure, sameSite: cookieSameSite, domain: cookieDomain, path: cookiePath, expires: cookieLifetime });
	};

	private writeConsentsToMemory: WriteConsentsFunction = async consents => {
		LogService.log.debug("Write consents to memory:", consents);

		this.inMemoryConsents = { ...consents };
	};

	/**
	 * Removes specified consent cookie
	 * @param cookieName
	 */
	private removeConsentsCookie: RemoveConsentsFunction = async cookieName => {
		LogService.log.debug("Remove consent cookie:", cookieName);

		Cookies.remove(cookieName);
	};

	/**
	 * Removes the in memory consent data.
	 */
	private removeConsentsMemory: RemoveConsentsFunction = async () => {
		LogService.log.debug("Remove consents from memory.");

		this.inMemoryConsents = undefined;
	};

	private readConsentsFromCookie = async (cookieName: string) => {
		const consentCookie: ConsentDataFormat = Cookies.getJSON(cookieName) as ConsentDataFormat;

		LogService.log.debug("Read consents from cookie:", consentCookie);

		return consentCookie;
	};

	private readConsentsFromMemory = async () => {
		LogService.log.debug("Read consents from memory:", this.inMemoryConsents);

		return this.inMemoryConsents;
	};

	/**
	 * Returns the given array without duplicate objects so every object exists only once
	 * @param array
	 * @private
	 */
	private removeObjectDuplicatesInArray(array: any[]): any {
		return array.filter((consent, index) => {
			const _consent = JSON.stringify(consent);

			return (
				index ===
				array.findIndex(consentObject => {
					return JSON.stringify(consentObject) === _consent;
				})
			);
		});
	}

	/**
	 * Returns one boolean consent value from an array if there are no multiple entries with the same consent name. If
	 * there is no consent available it returns undefined.
	 * @param consents
	 * @param nameFilter
	 * @private
	 */
	private filterAndReturnSingleConsentValue(consents: ConsentType[], nameFilter: string): boolean {
		const filteredConsents = consents.filter(consent => consent.name === nameFilter);
		let consentResult: boolean = undefined;

		if (filteredConsents.length > 1) {
			throw new Error(`There are multiple '${nameFilter}' consents - there can be only one ⚔`);
		} else if (filteredConsents.length === 0) {
			consentResult = undefined;
		} else {
			consentResult = filteredConsents[0].userConsents;
		}

		return consentResult;
	}

	/**
	 * Checks if there are multiple custom consents that have an identical name. Returns the array if successful.
	 * @param customConsents
	 * @private
	 */
	private checkForDuplicateCustomConsentNames(customConsents: ConsentType[]): ConsentType[] {
		let keyStack: string[] = [];

		return customConsents.map(consent => {
			if (keyStack.includes(consent.name)) {
				throw new Error("There are multiple custom consents with the same name and different values - each custom consent must have an individual name.");
			}

			keyStack.push(consent.name);

			return consent;
		});
	}

	/**
	 * Returns a consent data format object with the given parameters, removes identical consent duplicates and checks
	 * for identically named consents with different content.
	 * @param consents
	 * @param timeToLive
	 * @param dataFormatVersion
	 * @private
	 */
	private createConsentDataFormat(consents: ConsentType[], timeToLive: number, dataFormatVersion: number): ConsentDataFormat {
		const timestamp = Date.now();
		const noDuplicateConsents: ConsentType[] = this.removeObjectDuplicatesInArray(consents);
		const marketingConsent: boolean = this.filterAndReturnSingleConsentValue(noDuplicateConsents, StandardConsent.Marketing);
		const convenienceConsent: boolean = this.filterAndReturnSingleConsentValue(noDuplicateConsents, StandardConsent.Convenience);
		const analysisConsent: boolean = this.filterAndReturnSingleConsentValue(noDuplicateConsents, StandardConsent.Analysis);
		const customConsents: ConsentType[] = noDuplicateConsents.filter(
			consent => consent.name !== StandardConsent.Marketing && consent.name !== StandardConsent.Convenience && consent.name !== StandardConsent.Analysis,
		);
		const noDuplicateKeyCustomConsents: ConsentType[] = this.checkForDuplicateCustomConsentNames(customConsents);

		return {
			consent: {
				convenience: convenienceConsent,
				analysis: analysisConsent,
				marketing: marketingConsent,
				custom: noDuplicateKeyCustomConsents,
			},
			timeToLive: timeToLive,
			timestamp: timestamp,
			version: dataFormatVersion,
		};
	}
}

export const ConsentService = new ConsentServiceController();
